/**
 * Filtri specializzati per valori booleani e null
 *
 * @description
 * Questo modulo contiene i filtri per valori boolean e per controlli di nullità.
 * - BooleanFilter: per checkbox e switch
 * - NullFilter: per verificare se un campo è null
 * - NotNullFilter: per verificare se un campo non è null
 */

import { BaseFilter } from '../core/base-filter';
import { SearchOperator } from '../types';
import type { QueryParams } from '../types';

/**
 * Interfaccia per dati di filtro booleano
 * Utilizzabile per Vue 3 Composition API con oggetti plain reattivi
 */
export interface IBooleanFilterData {
	/** Nome della proprietà da filtrare */
	propertyName: string;
	/** Valore corrente del filtro */
	value: boolean | null;
	/** Valore di default del filtro */
	defaultValue: boolean | null;
	/** Nome della proprietà nella query string */
	queryPropertyName: string;
	/** Operatore di ricerca applicato */
	searchOperator: string;
	/** Se aggiornare la query string al cambio */
	updateQueryOnChange: boolean;
}

/**
 * Tipo combinato per filtro booleano con metodi fluenti
 */
export type BooleanFilterFactory = IBooleanFilterData & {
	/** Imposta il valore iniziale */
	initialValue(value: boolean | null): BooleanFilterFactory;
	/** Imposta il valore di default */
	default(value: boolean | null): BooleanFilterFactory;
	/** Configura aggiornamento query string */
	updateQueryOnFilterChange(value: boolean): BooleanFilterFactory;
	/** Configura nome proprietà query string */
	setQueryPropertyName(value: string): BooleanFilterFactory;
	/** Operatore "uguale" */
	equals(): BooleanFilterFactory;
	/** Operatore "diverso da" */
	notEquals(): BooleanFilterFactory;
};

/**
 * Filtro per valori booleani
 *
 * @description
 * Ottimizzato per checkbox, switch e altri controlli boolean.
 * Gestisce automaticamente la conversione da stringhe ('true'/'false') a boolean.
 *
 * @example
 * // Filtro per campo "attivo"
 * const activeFilter = new BooleanFilter('active')
 *   .equals()           // WHERE active = true/false
 *   .default(false);    // Default: false (non appare nell'URL)
 *
 * @example
 * // Filtro per utenti abilitati
 * const enabledFilter = new BooleanFilter('user.enabled')
 *   .equals()
 *   .initialValue(true) // Inizia con valore true
 *   .updateQueryOnFilterChange(true);
 *
 * @category Filters
 * @since 1.0.0
 */
export class BooleanFilter extends BaseFilter<boolean | null> {
	/**
	 * Crea una nuova istanza di BooleanFilter
	 *
	 * @param propertyName - Nome della proprietà da filtrare
	 *
	 * @example
	 * const filter = new BooleanFilter('isActive');
	 *
	 * @public
	 */
	constructor(propertyName: string) {
		super(Boolean, propertyName);
	}

	/**
	 * Deserializza il valore dalla query string
	 * Converte stringhe ('true', 'false') in valori boolean
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?active=true
	 * filter._fromQuery({ active: 'true' });
	 * // filter.value diventa true
	 *
	 * // URL: ?active=false
	 * filter._fromQuery({ active: 'false' });
	 * // filter.value diventa false
	 *
	 * @protected
	 * @override
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined) {
			// FIX: Gestisci 'null' letterale dalla query string
			if (value === 'null') {
				this.value = null;
				return;
			}

			// Converte stringhe e altri valori in boolean
			// Gestisce sia boolean che stringhe dalla query
			const stringValue = String(value);
			this.value = !!(value && (typeof value === 'boolean' ? value : stringValue === 'true'));
		}
	}

	/**
	 * Serializza il valore per la query string
	 * Converte boolean in stringa per l'URL
	 *
	 * @returns 'true' o 'false' come stringa
	 *
	 * @example
	 * filter.value = true;
	 * filter._getSerializedValue(); // 'true'
	 *
	 * @protected
	 * @override
	 */
	protected _getSerializedValue(): string | undefined {
		return this.value !== null && this.value !== undefined ? String(this.value) : undefined;
	}
}

/**
 * Filtro per verificare valori null
 *
 * @description
 * Filtro specializzato che verifica se un campo è null o meno.
 * L'operatore di ricerca è fisso a ISNULL e non può essere cambiato.
 *
 * @example
 * // Filtro per record senza data di fine
 * const noEndDateFilter = new NullFilter('endDate');
 * noEndDateFilter.value = true;  // Cerca record con endDate = null
 *
 * @example
 * // Filtro per ordini senza cliente assegnato
 * const noCustomerFilter = new NullFilter('customerId')
 *   .initialValue(false)  // Default: cerca record con customerId != null
 *   .updateQueryOnFilterChange(true);
 *
 * @category Filters
 * @since 1.0.0
 */
export class NullFilter extends BooleanFilter {
	/**
	 * Crea una nuova istanza di NullFilter
	 *
	 * @param propertyName - Nome della proprietà da controllare per nullità
	 *
	 * @example
	 * const filter = new NullFilter('deletedAt');
	 * // Cerca record dove deletedAt è null (non eliminati)
	 *
	 * @public
	 */
	constructor(propertyName: string) {
		super(propertyName);
		// Imposta l'operatore fisso a ISNULL
		(this as any)._searchOperator = SearchOperator.ISNULL;
	}

	/**
	 * Override del metodo _filter per mantenere sempre ISNULL
	 *
	 * @param operator - Operatore ignorato
	 * @returns L'istanza del filtro
	 *
	 * @protected
	 * @override
	 */
	protected _filter(_operator: any): this {
		// Ignora l'operatore passato e mantiene sempre ISNULL
		(this as any)._searchOperator = SearchOperator.ISNULL;
		return this;
	}
}

/**
 * Filtro per verificare valori non-null
 *
 * @description
 * Filtro specializzato che verifica se un campo non è null.
 * L'operatore di ricerca è fisso a ISNOTNULL e non può essere cambiato.
 * È il complementare di NullFilter.
 *
 * @example
 * // Filtro per record con data di completamento
 * const completedFilter = new NotNullFilter('completedAt');
 * completedFilter.value = true;  // Cerca record con completedAt != null
 *
 * @example
 * // Filtro per utenti con avatar
 * const hasAvatarFilter = new NotNullFilter('user.avatarUrl')
 *   .initialValue(true)   // Default: cerca utenti con avatar
 *   .queryPropertyName('hasAvatar');
 *
 * @category Filters
 * @since 1.0.0
 */
export class NotNullFilter extends BooleanFilter {
	/**
	 * Crea una nuova istanza di NotNullFilter
	 *
	 * @param propertyName - Nome della proprietà da controllare per non-nullità
	 *
	 * @example
	 * const filter = new NotNullFilter('approvedAt');
	 * // Cerca record dove approvedAt non è null (già approvati)
	 *
	 * @public
	 */
	constructor(propertyName: string) {
		super(propertyName);
		// Imposta l'operatore fisso a ISNOTNULL
		(this as any)._searchOperator = SearchOperator.ISNOTNULL;
	}

	/**
	 * Override del metodo _filter per mantenere sempre ISNOTNULL
	 *
	 * @param operator - Operatore ignorato
	 * @returns L'istanza del filtro
	 *
	 * @protected
	 * @override
	 */
	protected _filter(_operator: any): this {
		// Ignora l'operatore passato e mantiene sempre ISNOTNULL
		(this as any)._searchOperator = SearchOperator.ISNOTNULL;
		return this;
	}
}

/**
 * Factory function per creare filtri booleani tipizzati
 * Compatibile con Vue 3 Composition API e completamente reattivo
 *
 * @param propertyName - Nome della proprietà da filtrare
 * @returns Oggetto filtro booleano tipizzato con API fluente
 *
 * @example
 * // Per Vue 3 Composition API
 * const activeFilter = ref(createBooleanFilterData('active').equals());
 *
 * @example
 * // Con configurazione completa
 * const enabledFilter = ref(
 *   createBooleanFilterData('user.enabled')
 *     .equals()
 *     .initialValue(true)
 *     .updateQueryOnFilterChange(true)
 * );
 */
export function createBooleanFilterData(propertyName: string): BooleanFilterFactory {
	// Sanitizza nome proprietà per query string
	const sanitizedName = propertyName.replace(/[.[\\]]/g, '_').toLowerCase();

	// Oggetto combinato con dati e metodi
	const filter: BooleanFilterFactory = {
		propertyName,
		value: null,
		defaultValue: null,
		queryPropertyName: sanitizedName,
		searchOperator: 'NOOP',
		updateQueryOnChange: false,

		initialValue(value: boolean | null): BooleanFilterFactory {
			filter.value = value;
			return filter;
		},

		default(value: boolean | null): BooleanFilterFactory {
			filter.defaultValue = value;
			return filter;
		},

		updateQueryOnFilterChange(value: boolean): BooleanFilterFactory {
			filter.updateQueryOnChange = value;
			return filter;
		},

		setQueryPropertyName(value: string): BooleanFilterFactory {
			filter.queryPropertyName = value;
			return filter;
		},

		equals(): BooleanFilterFactory {
			filter.searchOperator = 'EQUAL';
			return filter;
		},

		notEquals(): BooleanFilterFactory {
			filter.searchOperator = 'NOT_EQUAL';
			return filter;
		},
	};

	return filter;
}
